/**
 * "Official system unavailable" banner. Shown when the monitor (cloudflare/monitor, /api/status)
 * reports the city's planning API down, or when this browser sees repeated upstream failures;
 * hidden again on the next successful request.
 */
import { icon, esc } from './ui.js';

let el = null;
let shown = null;       // 'monitor' | 'client' | null
let dismissed = false;
let fails = [];
let checking = false;
let onRetry = null;

export function initStatus({ retry } = {}) {
  onRetry = retry || null;
  if (document.querySelector('meta[name="qh-backend"]')?.content !== 'edge') return;
  const check = () => fetchStatus().then((s) => { if (s?.upstream?.state === 'down') show('monitor', s.upstream.since); });
  (window.requestIdleCallback || ((f) => setTimeout(f, 1200)))(check, { timeout: 4000 });
}

async function fetchStatus() {
  try {
    const r = await fetch('/api/status', { headers: { accept: 'application/json' } });
    return r.ok ? await r.json() : null;
  } catch { return null; }
}

/** Called by the API layer for every upstream request outcome. */
export function noteUpstream(ok, err) {
  const now = Date.now();
  if (ok) {
    fails = [];
    if (shown) hide();
    return;
  }
  const st = err?.status ?? 0;
  if ((st && st < 500 && st !== 429) || navigator.onLine === false) return; // our request / our network, not an outage
  fails = fails.filter((t) => now - t < 120000);
  fails.push(now);
  if (shown) return;
  // 2 failures: ask the monitor; 4 failures within 2 minutes: say so even if it has not noticed yet
  if (fails.length >= 4) { show('client'); return; }
  if (fails.length === 2 && !checking) {
    checking = true;
    fetchStatus().then((s) => { if (s?.upstream?.state === 'down') show('monitor', s.upstream.since); }).finally(() => { checking = false; });
  }
}

function show(kind, since) {
  if (dismissed || shown) return;
  shown = kind;
  if (!el) {
    el = document.createElement('div');
    el.className = 'net-banner glass';
    el.setAttribute('role', 'status');
    document.body.appendChild(el);
    el.addEventListener('click', (e) => {
      const b = e.target.closest('[data-act]');
      if (!b) return;
      if (b.dataset.act === 'close') { dismissed = true; hide(); }
      if (b.dataset.act === 'retry') { fails = []; hide(); onRetry?.(); }
    });
  }
  const mins = since ? Math.max(1, Math.round((Date.now() - since) / 60000)) : 0;
  const when = mins ? (mins < 90 ? ` từ khoảng ${mins} phút trước` : ` từ khoảng ${Math.round(mins / 60)} giờ trước`) : '';
  const head = kind === 'monitor' ? `Hệ thống thông tin quy hoạch của Thành phố đang gián đoạn${when}` : 'Máy chủ dữ liệu quy hoạch của Thành phố đang phản hồi chậm hoặc lỗi';
  el.innerHTML = `<span class="nb-ico">${icon('cloud-off')}</span>
    <div class="nb-text"><b>${esc(head)}</b>
    <span>Thông tin ô đất có thể tải chậm hoặc chưa hiện. Bản đồ nền, nhãn, danh mục đồ án vẫn dùng được.</span></div>
    <button type="button" class="btn sm subtle" data-act="retry">${icon('rotate-ccw')}Thử lại</button>
    <button type="button" class="icon-btn ghost sm" data-act="close" aria-label="Ẩn thông báo">${icon('x')}</button>`;
  el.hidden = false;
  document.body.classList.add('has-net-banner');
}

function hide() {
  shown = null;
  if (el) el.hidden = true;
  document.body.classList.remove('has-net-banner');
}

export const statusBannerShown = () => !!shown;
